import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { CalendarPlus, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRealtimeEvents } from "@/hooks/useRealtimeEvents";
import { AppointmentBookingModal } from "@/components/AppointmentBookingModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POS_KEY = "ca_book_float_pos";
const HIDDEN_KEY = "ca_book_float_hidden_until";

/**
 * Floating CTA shown when the client has a fully-paid service that does NOT
 * yet have an appointment booked.
 *
 * Behaviours:
 *  • Draggable — position persists in localStorage.
 *  • Closeable via small ✕ — hides until next paid plan arrives (24h grace).
 *  • Click → opens booking modal AND hides the float (re-evaluated after close).
 */
export const BookAppointmentFloat = () => {
  const { user, authHeader, isUser } = useAuth();
  const [bookablePlans, setBookablePlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [activePlan, setActivePlan] = useState(null);
  useEffect(() => {
    const h = () => { setHidden(false); setOpen(true); };
    window.addEventListener("ca:open-book", h);
    return () => window.removeEventListener("ca:open-book", h);
  }, []);
  const [hidden, setHidden] = useState(false);
  const [pos, setPos] = useState({ x: 16, y: 180 });
  const dragRef = useRef({ dragging: false, moved: false });

  // Load saved position & hidden state
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(POS_KEY) || "null");
      if (saved && typeof saved.x === "number" && typeof saved.y === "number") {
        setPos({ x: saved.x, y: saved.y });
      }
      const until = Number(localStorage.getItem(HIDDEN_KEY) || 0);
      if (until && until > Date.now()) setHidden(true);
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    if (!isUser) return;
    try {
      const r = await axios.get(`${API}/users/payment-plans`, { headers: authHeader() });
      const plans = (r.data || []).filter(
        (p) => p.fully_paid && !p.appointment_id
      );
      setBookablePlans(plans);
      // If a new plan arrived, un-hide the float automatically.
      if (plans.length > 0) {
        const until = Number(localStorage.getItem(HIDDEN_KEY) || 0);
        if (until && until <= Date.now()) {
          localStorage.removeItem(HIDDEN_KEY);
          setHidden(false);
        }
      }
    } catch { /* noop */ }
  }, [isUser, authHeader]);

  useEffect(() => { load(); }, [load]);

  useRealtimeEvents({
    payment_request_decided: (d) => { if (d?.status === "approved") load(); },
    notification: (d) => { if (d?.category === "payment") load(); },
  });

  const dismiss = (e) => {
    e?.stopPropagation();
    // Hide for 24h (will reappear if a new fully_paid plan arrives meanwhile).
    localStorage.setItem(HIDDEN_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setHidden(true);
  };

  const openBooking = () => {
    if (dragRef.current.moved) {
      // The pointer-up came from a drag, not a tap — don't trigger the action.
      dragRef.current.moved = false;
      return;
    }
    setActivePlan(bookablePlans[0]);
    setOpen(true);
    // Hide the floating button while the modal is open; restore on close
    // (reload covers the case where the appointment was actually booked).
    setHidden(true);
  };

  // ---- Drag handling (pointer events for touch + mouse uniformity) ----
  const onPointerDown = (e) => {
    e.preventDefault();
    dragRef.current.dragging = true;
    dragRef.current.moved = false;
    dragRef.current.startX = e.clientX - pos.x;
    dragRef.current.startY = e.clientY - pos.y;
    e.target.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const nx = e.clientX - dragRef.current.startX;
    const ny = e.clientY - dragRef.current.startY;
    const maxX = window.innerWidth - 220;
    const maxY = window.innerHeight - 60;
    const clamped = {
      x: Math.max(4, Math.min(maxX, nx)),
      y: Math.max(80, Math.min(maxY, ny)),
    };
    if (Math.abs(nx - pos.x) > 4 || Math.abs(ny - pos.y) > 4) {
      dragRef.current.moved = true;
    }
    setPos(clamped);
  };
  const onPointerUp = (e) => {
    if (dragRef.current.dragging) {
      dragRef.current.dragging = false;
      try { localStorage.setItem(POS_KEY, JSON.stringify(pos)); } catch { /* noop */ }
      e.target.releasePointerCapture?.(e.pointerId);
    }
  };

  if (!user || !isUser || bookablePlans.length === 0 || hidden) {
    // Even when hidden, keep the modal mountable in case it's open
    return (
      <>
        {open && activePlan && (
          <AppointmentBookingModal
            open={open}
            onClose={() => {
              setOpen(false);
              // When modal closes, reload the plans — if the user actually
              // booked, the plan will have `appointment_id` and we won't
              // re-show. Also unhide so a new plan can show the float.
              localStorage.removeItem(HIDDEN_KEY);
              setHidden(false);
              load();
            }}
            plan={activePlan}
          />
        )}
      </>
    );
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="book-float"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.6 }}
          style={{ left: pos.x, top: pos.y }}
          className="fixed z-[70] touch-none select-none"
          data-testid="book-appointment-float"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <div className="relative inline-flex items-center gap-2 pl-3 pr-2 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-emerald-700 text-white shadow-[0_0_30px_rgba(16,185,129,0.55)] border border-emerald-300/50 font-semibold uppercase tracking-widest text-[11px] cursor-grab active:cursor-grabbing">
            <span className="absolute inset-0 rounded-full border-2 border-emerald-300/60 animate-ping pointer-events-none" />
            <button
              onClick={openBooking}
              data-testid="book-appointment-open"
              className="inline-flex items-center gap-2 pr-1"
            >
              <CalendarPlus className="w-4 h-4 shrink-0" />
              <span className="whitespace-nowrap">
                Agendar tu cita
                {bookablePlans.length > 1 && (
                  <span className="ml-1.5 px-1.5 py-0.5 bg-white/20 rounded-full text-[10px]">
                    {bookablePlans.length}
                  </span>
                )}
              </span>
            </button>
            <button
              onClick={dismiss}
              data-testid="book-appointment-dismiss"
              aria-label="Ocultar"
              className="ml-1 h-6 w-6 rounded-full bg-black/30 hover:bg-black/50 flex items-center justify-center text-white"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        </motion.div>
      </AnimatePresence>

      {open && activePlan && (
        <AppointmentBookingModal
          open={open}
          onClose={() => {
            setOpen(false);
            localStorage.removeItem(HIDDEN_KEY);
            setHidden(false);
            load();
          }}
          plan={activePlan}
        />
      )}
    </>
  );
};
